import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ensureGifLoops } from '@src/util/gif-loop'
import './exercise-preview.sass'

const DEFAULT_PICTURE = '/assets/icon/brand/default-exercise.jpg'
// a GIF that takes longer than this no longer holds its card back, the placeholder stays until the GIF arrives
export const READY_TIMEOUT = 4000

/**
 * Preview of an exercise card: the GIF, patched in the browser so that it loops (some GIFs lack the loop marker),
 * the video when there is no GIF, or the default picture. A placeholder stands in while the GIF downloads. If the GIF
 * cannot be patched it is shown as it is. Emits `ready` once there is a picture to show, so the card can wait for it.
 */
@Component
export class ExercisePreview extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse

  private objectUrl = ''
  private failed = false
  private gone = false
  private ready = false
  private readyTimer = 0

  get videoURLs (): { webm, mp4, gif } {
    // an exercise loaded without its videos has no preview
    return this.exercise.videos ? this.exercise.getVideoURLs() : { webm: '', mp4: '', gif: '' }
  }

  get gifUrl (): string {
    return this.videoURLs.gif
  }

  @Watch('gifUrl', { immediate: true })
  async load () {
    this.release()
    this.failed = false
    const gif = this.gifUrl
    if (!gif) return this.markReady()
    if (!this.ready && !this.readyTimer) this.readyTimer = window.setTimeout(this.markReady, READY_TIMEOUT)

    try {
      const response = await fetch(gif, { mode: 'cors', credentials: 'omit' })
      if (!response.ok) throw new Error('gif not loaded')
      const bytes = ensureGifLoops(new Uint8Array(await response.arrayBuffer()))
      const url = URL.createObjectURL(new Blob([bytes], { type: 'image/gif' }))
      if (this.gone || gif !== this.gifUrl) {
        URL.revokeObjectURL(url)
        return
      }
      this.objectUrl = url
    } catch (error) {
      // a late failure of a GIF that is no longer shown must not touch the current one
      if (gif === this.gifUrl) this.failed = true
    }
  }

  beforeDestroy () {
    this.gone = true
    window.clearTimeout(this.readyTimer)
    this.release()
  }

  markReady () {
    window.clearTimeout(this.readyTimer)
    if (this.ready) return
    this.ready = true
    this.$emit('ready')
  }

  release () {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = ''
  }

  showOriginal () {
    this.release()
    this.failed = true
  }

  render (h: CreateElement) {
    const videoURLs = this.videoURLs
    if (videoURLs.gif) {
      if (this.objectUrl) return <img key='looping' class='exercise-preview' src={this.objectUrl} onLoad={this.markReady} onError={this.showOriginal} />
      if (this.failed) return <img key='original' class='exercise-preview' src={videoURLs.gif} onLoad={this.markReady} attrs-onerror={`this.src="${DEFAULT_PICTURE}"`} />
      // a placeholder, not an img without src, which the browser draws with a frame until the GIF is ready
      return <div key='loading' class='exercise-preview exercise-preview-placeholder skeleton' />
    }
    if (videoURLs.mp4 || videoURLs.webm) {
      return <video class='exercise-preview' autoplay='autoplay' loop='loop' poster={DEFAULT_PICTURE} muted>
        <source src={videoURLs.webm} type='video/webm' />
        <source src={videoURLs.mp4} type='video/mp4' />
        Your browser does not support the video tag.
      </video>
    }
    return <img class='exercise-preview' src={DEFAULT_PICTURE} />
  }
}
